import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';
import '../assets/css/premium-companies-grid.css';

/**
 * Premium Companies Grid Component
 * Displays premium offers grouped by company in a grid layout
 */
const PremiumCompaniesGrid = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchPremiumOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('🔍 PremiumCompaniesGrid: Fetching premium offers...');
        
        const response = await axiosClient.get('/posts', {
          params: {
            op: 'premium',
            perPage: 50
          }
        });
        
        console.log('✅ PremiumCompaniesGrid: API Response:', response.data);
        
        if (mounted) {
          const data = response.data?.result?.data || response.data?.data || [];
          
          // Group by company
          const groupedByCompany = data.reduce((acc, offer) => {
            const companyName = offer.company_name || 'Entreprise inconnue';
            
            // Accès correct au logo medium avec logs
            let companyLogo = null;
            
            // Essayer d'abord logo_url direct
            if (offer.logo_url && typeof offer.logo_url === 'object') {
              companyLogo = offer.logo_url.medium || offer.logo_url.full || offer.logo_url.small;
            }
            
            // Sinon essayer company.logo_url
            if (!companyLogo && offer.company?.logo_url && typeof offer.company.logo_url === 'object') {
              companyLogo = offer.company.logo_url.medium || offer.company.logo_url.full || offer.company.logo_url.small;
            }
            
            // Remplacer https://localhost par http://localhost pour éviter les erreurs SSL
            if (companyLogo && companyLogo.includes('https://localhost')) {
              companyLogo = companyLogo.replace('https://localhost', 'http://localhost');
            }
            
            console.log(`Logo for ${companyName}:`, companyLogo);
            
            if (!acc[companyName]) {
              acc[companyName] = {
                name: companyName,
                logo: companyLogo,
                count: 0,
                offers: []
              };
            }
            
            acc[companyName].count++;
            acc[companyName].offers.push(offer);
            
            return acc;
          }, {});
          
          // Convert to array and sort by count
          const companiesArray = Object.values(groupedByCompany)
            .sort((a, b) => b.count - a.count);
          
          setCompanies(companiesArray);
          console.log('📊 PremiumCompaniesGrid: Loaded', companiesArray.length, 'companies');
          console.log('🖼️ First company logo:', companiesArray[0]?.logo);
        }
      } catch (err) {
        console.error('❌ PremiumCompaniesGrid: Error:', err);
        if (mounted) {
          setError('Impossible de charger les offres premium');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPremiumOffers();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="premium-companies-grid">
        <div className="grid-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des entreprises...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="premium-companies-grid">
        <div className="grid-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!companies || companies.length === 0) {
    return (
      <div className="premium-companies-grid">
        <div className="grid-empty">
          <p>Aucune entreprise premium pour le moment</p>
        </div>
      </div>
    );
  }

  const displayedCompanies = showAll ? companies : companies.slice(0, 12);

  const handleCardClick = (company) => {
    // Naviguer vers la première offre de l'entreprise
    if (company.offers && company.offers.length > 0) {
      const firstOffer = company.offers[0];
      navigate(`/job-detail?id=${firstOffer.id}`);
    }
  };

  return (
    <div className="premium-companies-grid">
      <div className="companies-grid">
        {displayedCompanies.map((company, index) => (
          <div 
            key={index} 
            className="company-card"
            onClick={() => handleCardClick(company)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleCardClick(company);
              }
            }}
          >
            <div className="company-logo-wrapper">
              {company.logo ? (
                <img 
                  src={company.logo} 
                  alt={company.name}
                  className="company-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="company-logo-placeholder" 
                style={{ display: company.logo ? 'none' : 'flex' }}
              >
                {company.name.charAt(0).toUpperCase()}
              </div>
            </div>
            
            <div className="company-info">
              <h3 className="company-name" title={company.name}>
                {company.name}
              </h3>
              <p className="company-count">
                <strong>{company.count}</strong> {company.count > 1 ? 'offres' : 'offre'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {companies.length > 12 && (
        <div className="grid-footer">
          <button 
            className="view-more-btn"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Voir moins' : `Voir plus (${companies.length - 12} autres)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default PremiumCompaniesGrid;
